package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateListOf
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.icons.fa.FaIcon
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.browser.window
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px

data class ToastItem(
    val id: String,
    val message: String,
    val title: String? = null,
    val variant: AlertVariant = AlertVariant.INFO
)

object ToastManager {
    val toasts = mutableStateListOf<ToastItem>()
    private var counter = 0

    fun show(
        message: String,
        title: String? = null,
        variant: AlertVariant = AlertVariant.INFO,
        durationMs: Int = 4000
    ) {
        val id = "toast-${++counter}-${window.performance.now()}"
        val item = ToastItem(id = id, message = message, title = title, variant = variant)
        toasts.add(item)

        window.setTimeout({
            dismiss(id)
        }, durationMs)
    }

    fun info(message: String, title: String? = null) = show(message, title, AlertVariant.INFO)
    fun success(message: String, title: String? = null) = show(message, title, AlertVariant.SUCCESS)
    fun warning(message: String, title: String? = null) = show(message, title, AlertVariant.WARNING)
    fun error(message: String, title: String? = null) = show(message, title, AlertVariant.DANGER)

    fun dismiss(id: String) {
        toasts.removeAll { it.id == id }
    }
}

@Composable
fun ToastContainer() {
    val theme = LocalAppTheme.current
    val palette = theme.colors

    if (ToastManager.toasts.isEmpty()) return

    Column(
        modifier = Modifier
            .position(Position.Fixed)
            .bottom(1.5.cssRem)
            .right(1.5.cssRem)
            .zIndex(9999)
            .gap(0.75.cssRem)
    ) {
        ToastManager.toasts.forEach { toast ->
            val (accentColor, bgColor) = when (toast.variant) {
                AlertVariant.INFO -> palette.statusProcessing to palette.surfaceElevated
                AlertVariant.SUCCESS -> palette.statusReady to palette.surfaceElevated
                AlertVariant.WARNING -> palette.statusPending to palette.surfaceElevated
                AlertVariant.DANGER -> palette.statusFailed to palette.surfaceElevated
            }

            Row(
                modifier = Modifier
                    .backgroundColor(bgColor)
                    .borderRadius(theme.radii.md)
                    .border(width = 1.px, style = LineStyle.Solid, color = palette.border)
                    .borderLeft(width = 4.px, style = LineStyle.Solid, color = accentColor)
                    .boxShadow(0.px, 4.px, 24.px, color = Color.rgba(0, 0, 0, 0.4f))
                    .padding(leftRight = 1.cssRem, topBottom = 0.75.cssRem)
                    .minWidth(280.px)
                    .maxWidth(420.px)
                    .transition(Transition.of("all", 200.ms)),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(modifier = Modifier.margin(right = 0.65.cssRem)) {
                    when (toast.variant) {
                        AlertVariant.INFO -> FaIcon("circle-info", modifier = Modifier.color(accentColor).fontSize(1.1.cssRem))
                        AlertVariant.SUCCESS -> FaIcon("circle-check", modifier = Modifier.color(accentColor).fontSize(1.1.cssRem))
                        AlertVariant.WARNING -> FaIcon("triangle-exclamation", modifier = Modifier.color(accentColor).fontSize(1.1.cssRem))
                        AlertVariant.DANGER -> FaIcon("circle-exclamation", modifier = Modifier.color(accentColor).fontSize(1.1.cssRem))
                    }
                }

                Column(modifier = Modifier.weight(1f)) {
                    if (!toast.title.isNullOrBlank()) {
                        SpanText(
                            text = toast.title,
                            modifier = Modifier
                                .fontSize(0.85.cssRem)
                                .fontWeight(FontWeight.Bold)
                                .color(palette.textPrimary)
                        )
                    }
                    SpanText(
                        text = toast.message,
                        modifier = Modifier
                            .fontSize(0.8.cssRem)
                            .color(palette.textPrimary)
                    )
                }

                Box(
                    modifier = Modifier
                        .margin(left = 0.5.cssRem)
                        .cursor(Cursor.Pointer)
                        .onClick { ToastManager.dismiss(toast.id) }
                        .opacity(0.6)
                ) {
                    FaIcon("xmark", modifier = Modifier.color(palette.textSecondary).fontSize(0.85.cssRem))
                }
            }
        }
    }
}
